import React, { createContext, useContext } from 'react';

import { ALargeSmall, ChevronDown } from 'lucide-react';
import { useEditorSelector, usePath, useSelectionVersion } from 'platejs/react';
import { makeStyles } from 'tss-react/mui';

import { getEmptyParagraphAaBlock } from './utils/empty-paragraph';

/** Space reserved on the anchor line so a placeholder can't run under the toggle. */
export const AA_RESERVED_WIDTH = 64;

interface EmptyParagraphAaState {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

/**
 * Present only for editors that offer the empty-paragraph Aa affordance (the
 * selection-floating variant). Null elsewhere, so the shared paragraph renderer
 * stays inert in every other editor.
 */
const EmptyParagraphAaContext = createContext<EmptyParagraphAaState | null>(null);

export const EmptyParagraphAaProvider = EmptyParagraphAaContext.Provider;

const useStyles = makeStyles()(theme => ({
  button: {
    alignItems: 'center',
    background: theme.palette.common.white,
    border: 'none',
    borderRadius: 6,
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    gap: 2,
    padding: theme.spacing(0.5),
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },
}));

/**
 * The Aa toggle state for the paragraph currently rendering, or null when this
 * paragraph shouldn't show it (the editor doesn't offer the affordance, or the
 * caret is elsewhere). Paths are compared as keys so no path API is needed.
 */
export function useEmptyParagraphAaState(): EmptyParagraphAaState | null {
  const path = usePath();
  const state = useContext(EmptyParagraphAaContext);
  /**
   * `useEditorSelector` only re-runs when `versionEditor` bumps (value changes).
   * Arrow-key caret moves bump `versionSelection` alone, so subscribe to that
   * version in the deps list — recreating the selector atom forces a recompute
   * without a content change.
   */
  const selectionVersion = useSelectionVersion();
  const anchorPathKey = useEditorSelector(
    editor => getEmptyParagraphAaBlock(editor)?.[1].join('.') ?? '',
    [selectionVersion]
  );
  if (!state || !anchorPathKey) return null;

  return anchorPathKey === path.join('.') ? state : null;
}

/**
 * Aa toggle that opens the floating format toolbar for a blank paragraph. Rendered
 * inline by the paragraph element and pinned to the far right of its own line, so
 * it rides along with that line instead of being positioned against the editor box.
 */
export function EmptyParagraphAaToggle({ state }: { state: EmptyParagraphAaState }): JSX.Element {
  const { classes } = useStyles();

  return (
    <button
      aria-label="Formatting options"
      aria-pressed={state.open}
      className={classes.button}
      contentEditable={false}
      type="button"
      onClick={() => state.onOpenChange(!state.open)}
      onMouseDown={event => {
        // Keep the caret in the blank paragraph so the toggle doesn't unmount mid-click.
        event.preventDefault();
      }}
    >
      <ALargeSmall size={20} />
      <ChevronDown size={14} />
    </button>
  );
}
